import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Linkedin, Twitter, Instagram, Github, Send } from "lucide-react";
import { Logo } from "@/components/Logo";

const COLS = [
  {
    title: "Services",
    links: [
      { label: "AI & Automation", to: "/services" },
      { label: "Custom Software", to: "/services" },
      { label: "Web & Mobile Apps", to: "/services" },
      { label: "UI/UX & Branding", to: "/services" },
      { label: "Digital Marketing & SEO", to: "/services" },
      { label: "Cloud & Consulting", to: "/services" },
    ],
  },
  {
    title: "Products",
    links: [
      { label: "The Aurge Labs - LMS", to: "/products/lms" },
      { label: "The Aurge Labs - CRM", to: "/products/crm" },
      { label: "The Aurge Labs - School Management System", to: "/products/school-management-system" },
      { label: "All Products Overview", to: "/products" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Case Studies", to: "/#work" },
      { label: "Engagement Models", to: "/services" },
      { label: "FAQs", to: "/contact" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    toast.success("You're subscribed! Welcome to the Aurge circle.");
    setEmail("");
  };

  return (
    <footer data-testid="site-footer" className="bg-[#01193D] text-white relative overflow-hidden">
      <div className="absolute inset-0 dot-grid-light opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20 relative">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-12 border-b border-white/10 mb-14" data-testid="footer-top-band">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Let's build something <span className="text-gradient-electric">exceptional</span> →
          </h2>
          <Link
            to="/contact"
            data-testid="footer-cta-button"
            className="btn-gradient shrink-0 inline-flex items-center gap-2 text-white text-sm font-semibold px-7 py-3.5 rounded-full self-start lg:self-auto"
          >
            Start Your Project <Send size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-4 lg:col-span-3">
            <Logo light />
            <p className="mt-5 text-slate-300 text-sm leading-relaxed max-w-xs">
              Technology that empowers, not just supports. We build intelligent digital products that move businesses forward.
            </p>
            <div className="flex gap-2.5 mt-6">
              {[
                { Icon: Linkedin, label: "linkedin" },
                { Icon: Twitter, label: "twitter" },
                { Icon: Instagram, label: "instagram" },
                { Icon: Github, label: "github" },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#social"
                  data-testid={`footer-social-${label}`}
                  aria-label={label}
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#2563EB] hover:border-[#2563EB] transition-all"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="font-display font-semibold text-sm tracking-wide uppercase text-[#91E0F1] mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-slate-300 hover:text-[#09B2ED] transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="col-span-2 md:col-span-4 lg:col-span-3">
            <h4 className="font-display font-semibold text-sm tracking-wide uppercase text-[#91E0F1] mb-5">Contact & Newsletter</h4>
            <ul className="space-y-2.5 text-sm text-slate-300">
              <li>hello@theaurgelabs.com</li>
              <li>+1 (555) 012-3456</li>
              <li>100 Innovation Drive, Suite 400<br />San Francisco, CA 94105</li>
            </ul>
            <form onSubmit={subscribe} className="flex max-w-xs mt-5" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="newsletter-email-input"
                className="w-full bg-white/10 border border-white/15 rounded-l-xl px-4 py-3 text-sm placeholder:text-slate-400 focus:outline-none focus:border-[#09B2ED] transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-button"
                aria-label="Subscribe"
                className="btn-gradient rounded-r-xl px-4 flex items-center justify-center"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-14" data-testid="footer-trust-badges">
          {["NDA on Request", "GDPR-Ready", "24-Hour Response", "ISO-Aligned Practices"].map((b) => (
            <span key={b} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 bg-white/5 border border-white/10 px-3.5 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#09B2ED]" /> {b}
            </span>
          ))}
        </div>

        <div className="border-t border-white/10 mt-7 pt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} The Aurge Labs. All rights reserved.</p>
          <div className="flex items-center gap-2.5 text-xs text-slate-400">
            <a href="#privacy" className="hover:text-[#09B2ED] transition-colors">Privacy Policy</a>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <a href="#terms" className="hover:text-[#09B2ED] transition-colors">Terms of Service</a>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span>hello@theaurgelabs.com</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
